import { setIsError } from '@redux/ApiResponseStatusGlobalDisplay/slice';
import { FRENCH_PHONE_NUMBER_REGEX, FRENCH_SIRET_REGEX } from 'components/Form/validationSchema';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { ROUTES } from 'routing/routes';
import { analytics } from 'services/analytics/analytics';
import {
  ANALYTICS_PROVIDER_ACTIONS,
  ANALYTICS_PROVIDER_CATEGORIES,
  ANALYTICS_PROVIDER_LABELS,
} from 'services/analytics/config';
import { submitSubscriptionRequest } from 'services/api/subscriptionRequest/client';
import { PartialSubscriptionRequestBody } from 'services/api/subscriptionRequest/types';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';
import { logger } from 'services/logs/logger';
import * as yup from 'yup';

import { SubscriptionRequestFormKeys } from './subscriptionRequestFormKeys';

export const emptySubscriptionRequestInitialValues: PartialSubscriptionRequestBody = {
  [SubscriptionRequestFormKeys.LAST_NAME]: '',
  [SubscriptionRequestFormKeys.FIRST_NAME]: '',
  [SubscriptionRequestFormKeys.EMAIL]: '',
  [SubscriptionRequestFormKeys.PHONE_NUMBER]: '',
  [SubscriptionRequestFormKeys.COMPANY_NAME]: '',
  [SubscriptionRequestFormKeys.SIRET]: '',
  [SubscriptionRequestFormKeys.COMPANY_JOB]: '',
  [SubscriptionRequestFormKeys.VISITED_SITE]: '',
};

export const useSubscriptionRequestFormik = (initialValues?: PartialSubscriptionRequestBody) => {
  const subscriptionRequestFormIntl = useIntlByKey('subscription-request.form');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const requiredFieldMessage = subscriptionRequestFormIntl('required-field');
  const invalidFieldMessage = subscriptionRequestFormIntl('invalid-field');

  const validationSchema = yup.object().shape({
    [SubscriptionRequestFormKeys.LAST_NAME]: yup.string().required(requiredFieldMessage),
    [SubscriptionRequestFormKeys.FIRST_NAME]: yup.string().required(requiredFieldMessage),
    [SubscriptionRequestFormKeys.EMAIL]: yup
      .string()
      .email(invalidFieldMessage)
      .required(requiredFieldMessage),
    [SubscriptionRequestFormKeys.PHONE_NUMBER]: yup
      .string()
      .matches(FRENCH_PHONE_NUMBER_REGEX, invalidFieldMessage)
      .required(requiredFieldMessage),
    [SubscriptionRequestFormKeys.COMPANY_NAME]: yup.string().required(requiredFieldMessage),
    [SubscriptionRequestFormKeys.SIRET]: yup
      .string()
      .matches(FRENCH_SIRET_REGEX, invalidFieldMessage)
      .required(requiredFieldMessage),
    [SubscriptionRequestFormKeys.COMPANY_JOB]: yup.string().required(requiredFieldMessage),
    [SubscriptionRequestFormKeys.VISITED_SITE]: yup.string().required(requiredFieldMessage),
  });

  const onSubmit = async (values: PartialSubscriptionRequestBody) => {
    try {
      await submitSubscriptionRequest(values);
      analytics.event(
        ANALYTICS_PROVIDER_ACTIONS.SUBSCRIPTION_REQUEST_SUBMISSION_SUCCESS,
        ANALYTICS_PROVIDER_LABELS.ACTION_SUCCESS,
        ANALYTICS_PROVIDER_CATEGORIES.SUBSCRIPTION_REQUEST,
      );
      navigate(ROUTES.SUBSCRIPTION_REQUEST_SUCCESS);
    } catch (error) {
      logger.logError('Error submitting subscription request', error);
      dispatch(setIsError(true));
    }
  };

  return useFormik({
    initialValues: initialValues ?? emptySubscriptionRequestInitialValues,
    validationSchema,
    onSubmit,
  });
};
